import { AnimatePresence, motion } from "framer-motion";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLoaderData } from "react-router-dom";
import { DayInfoModal } from "../components/DayInfoModal";
import { DaySelector } from "../components/DaySelector";
import { FacebookIcon } from "../components/icons";
import { PerformanceCard } from "../components/PerformanceCard";
import { StageSelector } from "../components/StageSelector";
import { getCurrentDate, parsePerformanceTime } from "../lib/formatTime";
import { useActiveStageTracker } from "../lib/useActiveStageTracker";
import { useScrollToActiveStage } from "../lib/useScrollToActiveStage";
import { useTimetableParams } from "../lib/useTimetableParams";
import {
  getAllDays,
  getAllPerformances,
  getAllStages,
  getArtistById,
  getDayById,
  getStageById,
  getStageColor,
  loadFestivalData,
} from "../services/dataService";
import { getFavorites, onFavoritesChange } from "../services/favoritesService";
import type { Day, Performance, Stage } from "../types";

// Define the expected shape of the performance data
type PerformanceData = Performance & {
  artist?: {
    id: string;
    name: string;
    qualifier?: string | null;
    collective?: string | null;
    description?: string;
    genre?: string | null;
  };
  stage?: {
    id: string;
    name: string;
    description?: string;
  };
  day?: Day;
};

// Define the expected shape of the data from the loader
interface TimetableLoaderData {
  performances: PerformanceData[];
  days: Day[];
}

export const loadTimetableData = async (): Promise<TimetableLoaderData> => {
  await loadFestivalData();
  const performances = getAllPerformances();
  const enrichedPerformances = performances.map((performance) => ({
    ...performance,
    artist: getArtistById(performance.artistId),
    stage: getStageById(performance.stageId),
    day: getDayById(performance.dayId),
  }));

  const days = getAllDays();
  return { performances: enrichedPerformances, days };
};

// ── Helper Functions ──

const filterAndSortPerformances = (
  performances: PerformanceData[],
  activeDayId: string,
  favorites: string[],
  showFavoritesOnly: boolean,
): PerformanceData[] => {
  return performances
    .filter((performance) => {
      const matchesDay = performance.dayId === activeDayId;
      const isFavorite = favorites.includes(performance.id);
      const matchesFavorites = !showFavoritesOnly || isFavorite;

      return matchesDay && matchesFavorites;
    })
    .sort((a, b) => {
      const now = getCurrentDate();
      const aHasEnded = parsePerformanceTime(a.endTime, a.dayId) < now;
      const bHasEnded = parsePerformanceTime(b.endTime, b.dayId) < now;

      if (aHasEnded && !bHasEnded) return 1;
      if (!aHasEnded && bHasEnded) return -1;

      return (
        parsePerformanceTime(a.startTime, a.dayId).getTime() -
        parsePerformanceTime(b.startTime, b.dayId).getTime()
      );
    });
};

// ── Sub-components ──

interface EmptyStateProps {
  showFavoritesOnly: boolean;
  message?: string;
}

const EmptyState: React.FC<EmptyStateProps> = ({ showFavoritesOnly, message }) => (
  <div className="text-center py-8 bg-card border border-border rounded-xl p-4">
    <p className="text-muted">
      {message ||
        (showFavoritesOnly
          ? "Nincsenek mentett események ezen a napon."
          : "Nincs esemény ezen a napon.")}
    </p>
  </div>
);

interface MixedTimelineProps {
  performances: PerformanceData[];
  showFavoritesOnly: boolean;
}

const MixedTimeline: React.FC<MixedTimelineProps> = ({ performances, showFavoritesOnly }) => {
  if (performances.length === 0) {
    return <EmptyState showFavoritesOnly={showFavoritesOnly} />;
  }

  return (
    <div className="space-y-3 pb-8">
      <AnimatePresence mode="popLayout">
        {performances.map((performance) => (
          <motion.div
            key={performance.id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            layout
          >
            <PerformanceCard performance={performance} highlightActive />
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
};

interface PerStageTimelineProps {
  stages: Stage[];
  performances: PerformanceData[];
  showFavoritesOnly: boolean;
}

const PerStageTimeline: React.FC<PerStageTimelineProps> = ({
  stages,
  performances,
  showFavoritesOnly,
}) => {
  return (
    <div className="space-y-8 pb-8">
      {stages.map((stage) => {
        const stagePerformances = performances.filter((p) => p.stageId === stage.id);

        return (
          <section key={stage.id} id={`stage-section-${stage.id}`} className="scroll-mt-[170px]">
            <div className="flex items-center gap-2 mb-3 pb-1.5 border-b border-white/10">
              <span
                className="w-2.5 h-2.5 rounded-full inline-block"
                style={{ backgroundColor: getStageColor(stage.id) }}
              />
              <h2 className="font-['Unbounded'] text-[12px] font-extrabold uppercase tracking-wide text-white">
                {stage.name}
              </h2>
            </div>

            {stagePerformances.length === 0 ? (
              <div className="text-center py-5 bg-[#222222]/40 border border-white/5 rounded-xl p-4">
                <p className="text-xs text-muted">
                  {showFavoritesOnly
                    ? "Nincsenek mentett események ezen a helyszínen."
                    : "Nincs ma esemény ezen a helyszínen."}
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {stagePerformances.map((performance) => (
                  <PerformanceCard key={performance.id} performance={performance} highlightActive />
                ))}
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
};

interface DayInfoBarProps {
  day?: Day;
}

const DayInfoBar: React.FC<DayInfoBarProps> = ({ day }) => {
  const [isInfoOpen, setIsInfoOpen] = useState(false);

  if (!day?.description) {
    return null;
  }

  return (
    <>
      <div className="mb-4 flex gap-2 flex-wrap">
        <button
          type="button"
          onClick={() => setIsInfoOpen(true)}
          className="inline-flex items-center gap-1.5 rounded-xl border border-white/15 bg-white/5 px-3 py-1.5 text-xs font-semibold text-white transition-all hover:bg-white/10 active:scale-95"
        >
          <svg
            className="h-3.5 w-3.5 pointer-events-none"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="16" x2="12" y2="12" />
            <line x1="12" y1="8" x2="12.01" y2="8" />
          </svg>
          Infó
        </button>

        {day.facebookEvent ? (
          <a
            href={day.facebookEvent}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 rounded-xl border border-pink/35 bg-pink/15 px-3 py-1.5 text-xs font-semibold text-pink transition-all hover:bg-pink/25 active:scale-95"
          >
            <FacebookIcon className="h-3.5 w-3.5 fill-current text-current" />
            Esemény
          </a>
        ) : null}

        {day.ticketUrl ? (
          <a
            href={day.ticketUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 rounded-xl border border-main/35 bg-main/15 px-3 py-1.5 text-xs font-semibold text-main transition-all hover:bg-main/25 active:scale-95"
          >
            <svg
              className="h-3.5 w-3.5 pointer-events-none"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M2 9a1 1 0 0 1 1-1h18a1 1 0 0 1 1 1v2a2 2 0 0 0 0 4v2a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1v-2a2 2 0 0 0 0-4Z" />
            </svg>
            Jegyek
          </a>
        ) : null}
      </div>

      <DayInfoModal day={day} isOpen={isInfoOpen} onClose={() => setIsInfoOpen(false)} />
    </>
  );
};

// ── Main Page Component ──

export const TimetablePage: React.FC = () => {
  const { performances, days } = useLoaderData() as TimetableLoaderData;

  const {
    activeStageId,
    activeDayId,
    showFavoritesOnly,
    setActiveStageId,
    setActiveDayId,
    setShowFavoritesOnly,
    setActiveStageIdState,
    hasStageInUrl,
  } = useTimetableParams(days);

  const stages = getAllStages().filter((stage) => !stage.hidden && stage.id !== "merch");
  const [favorites, setFavorites] = useState<string[]>(getFavorites());
  const isScrollingRef = useRef(true);

  // Custom hook to handle 300ms layout delay and initial scroll to active stage section
  useScrollToActiveStage(activeStageId, performances, stages, isScrollingRef, hasStageInUrl);

  // Robust scrolling/position tracking custom hook
  useActiveStageTracker(activeStageId, stages, isScrollingRef, setActiveStageIdState);

  // Automatic migration for legacy 'favorites' activeStageId value
  useEffect(() => {
    if (activeStageId === "favorites") {
      setActiveStageId("all");
      setShowFavoritesOnly(true);
    }
  }, [activeStageId, setActiveStageId, setShowFavoritesOnly]);

  // Sync favorites in real-time if updated from subcomponents
  useEffect(() => {
    return onFavoritesChange(() => {
      setFavorites(getFavorites());
    });
  }, []);

  const isFirstDayRender = useRef(true);
  // biome-ignore lint/correctness/useExhaustiveDependencies: scroll when activeDayId changes
  useEffect(() => {
    if (isFirstDayRender.current) {
      isFirstDayRender.current = false;
      return;
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [activeDayId]);

  const handleDayChange = (dayId: string) => {
    setActiveDayId(dayId);
  };

  const handleStageChange = useCallback(
    (stageId: string) => {
      if (stageId === "all") {
        if (activeStageId === "all") {
          const prevStage = localStorage.getItem("lastActiveStageId_v2") || "nagyszinpad";
          setActiveStageId(prevStage);
          isScrollingRef.current = true;
          setTimeout(() => {
            const element = document.getElementById(`stage-section-${prevStage}`);
            if (element) {
              element.scrollIntoView({ behavior: "smooth" });
            }
            setTimeout(() => {
              isScrollingRef.current = false;
            }, 800);
          }, 50);
        } else {
          localStorage.setItem("lastActiveStageId_v2", activeStageId);
          setActiveStageId("all");
        }
      } else {
        setActiveStageId(stageId);
        localStorage.setItem("lastActiveStageId_v2", stageId);
        isScrollingRef.current = true;
        setTimeout(() => {
          const element = document.getElementById(`stage-section-${stageId}`);
          if (element) {
            element.scrollIntoView({ behavior: "smooth" });
          }
          setTimeout(() => {
            isScrollingRef.current = false;
          }, 800);
        }, 50);
      }
    },
    [activeStageId, setActiveStageId],
  );

  const sortedPerformances = filterAndSortPerformances(
    performances,
    activeDayId,
    favorites,
    showFavoritesOnly,
  );
  const activeDay = days.find((day) => day.id === activeDayId);

  if (days.length === 0) {
    return (
      <div className="p-4 text-center bg-card border border-border rounded-xl">
        <p className="text-muted">Nincs megjeleníthető nap.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Sticky Day & Stage Selector Container */}
      <div
        className="sticky top-[70px] z-40 backdrop-blur-md -mx-4 px-4 py-3 space-y-3 border-b border-white/5"
        style={{ backgroundColor: "rgba(22, 22, 22, 0.75)" }}
      >
        <DaySelector days={days} activeDayId={activeDayId} handleDayChange={handleDayChange} />
        <StageSelector
          stages={stages}
          activeStageId={activeStageId}
          handleStageChange={handleStageChange}
        />
      </div>

      <div className="w-full">
        <DayInfoBar day={activeDay} />
        {activeStageId === "all" ? (
          <MixedTimeline performances={sortedPerformances} showFavoritesOnly={showFavoritesOnly} />
        ) : (
          <PerStageTimeline
            stages={stages}
            performances={sortedPerformances}
            showFavoritesOnly={showFavoritesOnly}
          />
        )}
      </div>
    </div>
  );
};

export default TimetablePage;
